import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PlusIcon, TrashIcon, CheckCircleIcon, ExclamationTriangleIcon } from "@heroicons/react/24/solid";
import {
  getSkill,
  createSkill,
  updateSkill,
  submitSkill,
} from "../../api/advisorApi";

// Routes:
//   /advisor/skills/new  → mode='create' (no id)
//   /advisor/skills/:id  → mode='edit'   (load + patch)

const Card = ({ children, className = "" }) => (
  <div
    className={`p-5 sm:p-6 rounded-2xl bg-white/60 dark:bg-[#1a2332]/60 backdrop-blur-xl border border-white/40 dark:border-white/10 shadow-glass dark:shadow-glass-dark ${className}`}
  >
    {children}
  </div>
);

const Field = ({ label, hint, children }) => (
  <label className="block">
    <div className="text-xs font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-1.5">
      {label}
    </div>
    {children}
    {hint && <p className="text-[11px] text-gray-500 mt-1">{hint}</p>}
  </label>
);

const inputCls =
  "w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 outline-none focus:ring-2 focus:ring-indigo-500";

const RISK_BANDS = [
  { value: "low",  label: "Low",    desc: "Capital preservation, low volatility." },
  { value: "mid",  label: "Medium", desc: "Balanced growth and risk." },
  { value: "high", label: "High",   desc: "Growth-first, larger drawdowns OK." },
];

const ASSET_CLASSES = [
  { value: "equity",      label: "Equities" },
  { value: "etf",         label: "ETFs" },
  { value: "mutual_fund", label: "Mutual funds" },
  { value: "bond",        label: "Bonds" },
  { value: "crypto",      label: "Crypto" },
  { value: "cash",        label: "Cash / FDs" },
  { value: "real_estate", label: "Real estate" },
  { value: "commodity",   label: "Commodities" },
];

const JURISDICTIONS = [
  { value: "IN", label: "🇮🇳 India" },
  { value: "US", label: "🇺🇸 United States" },
  { value: "GB", label: "🇬🇧 United Kingdom" },
  { value: "CA", label: "🇨🇦 Canada" },
  { value: "AU", label: "🇦🇺 Australia" },
  { value: "SG", label: "🇸🇬 Singapore" },
  { value: "AE", label: "🇦🇪 UAE" },
];

const Banner = ({ kind, children }) => {
  const cls =
    kind === "error"
      ? "bg-red-500/10 text-red-500 border-red-500/40"
      : "bg-emerald-500/10 text-emerald-500 border-emerald-500/40";
  const Icon = kind === "error" ? ExclamationTriangleIcon : CheckCircleIcon;
  return (
    <div className={`flex items-start gap-2 p-3 rounded-lg border text-sm ${cls}`}>
      <Icon className="w-4 h-4 mt-0.5 shrink-0" />
      <div className="flex-1">{children}</div>
    </div>
  );
};

// Allocations are stored on the wire as {label: fraction in [0,1]}. The
// editor works in percent-rows for sanity. We convert at save.
const allocationsToRows = (alloc) => {
  if (!alloc || Object.keys(alloc).length === 0) return [{ label: "", percent: "" }];
  return Object.entries(alloc).map(([label, frac]) => ({
    label,
    percent: String(Math.round(Number(frac || 0) * 1000) / 10),
  }));
};

const rowsToAllocations = (rows) => {
  const out = {};
  for (const r of rows) {
    const label = (r.label || "").trim();
    if (!label) continue;
    const p = parseFloat(r.percent);
    if (!isFinite(p)) continue;
    out[label] = p; // Wire format accepts percent OR fraction; backend normalises.
  }
  return out;
};

const SkillEditor = () => {
  const { id } = useParams();
  const isCreate = !id;
  const navigate = useNavigate();

  const [loading, setLoading] = useState(!isCreate);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [banner, setBanner] = useState(null);

  const [skill, setSkill] = useState({
    name: "",
    summary: "",
    risk_band: "mid",
    asset_classes: [],
    ai_guidance: "",
    eligible_jurisdictions: [],
    status: "draft",
  });
  const [rows, setRows] = useState([{ label: "", percent: "" }]);

  useEffect(() => {
    if (isCreate) return;
    (async () => {
      try {
        const s = await getSkill(id);
        setSkill(s);
        setRows(allocationsToRows(s.allocations));
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isCreate]);

  const totalPercent = useMemo(
    () => rows.reduce((acc, r) => acc + (parseFloat(r.percent) || 0), 0),
    [rows],
  );
  const allocationValid =
    rows.every((r) => !r.label.trim() || (parseFloat(r.percent) || 0) > 0) &&
    (rows.every((r) => !r.label.trim()) || Math.abs(totalPercent - 100) < 0.5);

  const updateLocal = (k, v) => setSkill((s) => ({ ...s, [k]: v }));
  const toggleArr = (k, value) =>
    setSkill((s) => {
      const have = s[k] || [];
      return {
        ...s,
        [k]: have.includes(value) ? have.filter((x) => x !== value) : [...have, value],
      };
    });

  const flash = (kind, message) => {
    setBanner({ kind, message });
    setTimeout(() => setBanner(null), 4000);
  };

  const buildPayload = () => ({
    name: skill.name,
    summary: skill.summary || "",
    risk_band: skill.risk_band,
    asset_classes: skill.asset_classes || [],
    allocations: rowsToAllocations(rows),
    ai_guidance: skill.ai_guidance || "",
    eligible_jurisdictions: skill.eligible_jurisdictions || [],
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isCreate) {
        const created = await createSkill(buildPayload());
        navigate(`/advisor/skills/${created.id}`, { replace: true });
        flash("success", "Saved.");
      } else {
        const updated = await updateSkill(id, buildPayload());
        setSkill(updated);
        setRows(allocationsToRows(updated.allocations));
        flash("success", "Saved.");
      }
    } catch (err) {
      const data = err?.response?.data || {};
      const firstKey = Object.keys(data)[0];
      const msg = firstKey
        ? `${firstKey}: ${Array.isArray(data[firstKey]) ? data[firstKey][0] : data[firstKey]}`
        : "Could not save.";
      flash("error", msg);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitForReview = async () => {
    if (isCreate) return;
    setSubmitting(true);
    try {
      await submitSkill(id);
      flash("success", "Submitted for review.");
    } catch (err) {
      const data = err?.response?.data || {};
      flash("error", data.detail || "Could not submit.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Card>Loading…</Card>;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">
            {isCreate ? "New skill" : skill.name || "Skill"}
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            A skill defines how the AI specialists should approach this user's portfolio
            on your behalf. Subscribers can hold multiple skills — when two conflict, the
            agent surfaces both options.
          </p>
        </div>
      </div>

      {banner && <Banner kind={banner.kind}>{banner.message}</Banner>}

      <Card>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Name" hint="What subscribers will see on the plan card.">
            <input
              className={inputCls}
              value={skill.name || ""}
              onChange={(e) => updateLocal("name", e.target.value)}
              maxLength={120}
              placeholder="e.g. Quality Compounders, IN"
            />
          </Field>
          <Field label="Risk band">
            <select
              className={inputCls}
              value={skill.risk_band}
              onChange={(e) => updateLocal("risk_band", e.target.value)}
            >
              {RISK_BANDS.map((rb) => (
                <option key={rb.value} value={rb.value}>{rb.label} — {rb.desc}</option>
              ))}
            </select>
          </Field>
        </div>

        <div className="mt-4">
          <Field label="Summary" hint="One sentence shown on the plan card.">
            <input
              className={inputCls}
              value={skill.summary || ""}
              onChange={(e) => updateLocal("summary", e.target.value)}
              maxLength={300}
              placeholder="e.g. Long-term holders of cash-rich, low-debt Indian equity."
            />
          </Field>
        </div>
      </Card>

      <Card>
        <h2 className="text-lg font-bold mb-2">Asset classes</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Which buckets does this skill operate in?
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {ASSET_CLASSES.map((ac) => {
            const on = (skill.asset_classes || []).includes(ac.value);
            return (
              <label key={ac.value} className={`px-3 py-2 rounded-lg border text-sm cursor-pointer transition ${on ? "border-indigo-500 bg-indigo-500/10 text-indigo-500 font-semibold" : "border-gray-300 dark:border-white/15 hover:bg-gray-50 dark:hover:bg-white/5"}`}>
                <input
                  type="checkbox"
                  className="hidden"
                  checked={on}
                  onChange={() => toggleArr("asset_classes", ac.value)}
                />
                {ac.label}
              </label>
            );
          })}
        </div>
      </Card>

      <Card>
        <div className="flex items-end justify-between mb-2">
          <div>
            <h2 className="text-lg font-bold">Target allocation</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Define your target weights. Must sum to 100% (or leave entirely empty).
            </p>
          </div>
          <p className={`text-sm font-mono ${allocationValid ? "text-emerald-500" : "text-amber-500"}`}>
            Total: {totalPercent.toFixed(1)}%
          </p>
        </div>
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={i} className="flex gap-2">
              <input
                className={`${inputCls} flex-1`}
                value={r.label}
                onChange={(e) => {
                  const next = [...rows];
                  next[i] = { ...next[i], label: e.target.value };
                  setRows(next);
                }}
                placeholder="Bucket label — e.g. Large-cap, IT, Cash"
              />
              <input
                className={`${inputCls} w-28`}
                inputMode="decimal"
                value={r.percent}
                onChange={(e) => {
                  const next = [...rows];
                  next[i] = { ...next[i], percent: e.target.value };
                  setRows(next);
                }}
                placeholder="0"
              />
              <button
                type="button"
                onClick={() => setRows(rows.filter((_, idx) => idx !== i))}
                className="w-10 h-10 rounded-lg flex items-center justify-center text-red-500 hover:bg-red-500/10 shrink-0"
                title="Remove row"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => setRows([...rows, { label: "", percent: "" }])}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-indigo-500/40 bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 text-sm font-semibold hover:bg-indigo-500/20"
          >
            <PlusIcon className="w-4 h-4" />
            Add row
          </button>
        </div>
      </Card>

      <Card>
        <h2 className="text-lg font-bold mb-1">AI guidance</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
          Write as if instructing one of FinAI's specialists. This text is injected
          into the agent's system prompt for every subscriber's portfolio.
        </p>
        <Field label="" hint='Example: "When evaluating tech equities, favor low-debt high-FCF names. Avoid speculative biotech."'>
          <textarea
            className={inputCls}
            rows={6}
            value={skill.ai_guidance || ""}
            onChange={(e) => updateLocal("ai_guidance", e.target.value)}
            placeholder="Tell the agent how this strategy thinks about new positions, exits, risk…"
          />
        </Field>
      </Card>

      <Card>
        <h2 className="text-lg font-bold mb-2">Eligible jurisdictions</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Leave empty to make this skill available globally. Otherwise only subscribers
          in these jurisdictions will see plans containing it.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {JURISDICTIONS.map((j) => {
            const on = (skill.eligible_jurisdictions || []).includes(j.value);
            return (
              <label key={j.value} className={`px-3 py-2 rounded-lg border text-sm cursor-pointer transition ${on ? "border-indigo-500 bg-indigo-500/10 text-indigo-500 font-semibold" : "border-gray-300 dark:border-white/15 hover:bg-gray-50 dark:hover:bg-white/5"}`}>
                <input
                  type="checkbox"
                  className="hidden"
                  checked={on}
                  onChange={() => toggleArr("eligible_jurisdictions", j.value)}
                />
                {j.label}
              </label>
            );
          })}
        </div>
      </Card>

      <div className="flex flex-wrap gap-3 justify-end">
        <button
          type="button"
          onClick={() => navigate("/advisor/skills")}
          className="px-4 py-2 rounded-lg border border-gray-300 dark:border-white/15 text-sm font-medium hover:bg-gray-50 dark:hover:bg-white/5"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !skill.name?.trim()}
          className="px-4 py-2 rounded-lg border border-indigo-500/40 bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 text-sm font-semibold hover:bg-indigo-500/20 disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save draft"}
        </button>
        {!isCreate && skill.status === "draft" && (
          <button
            type="button"
            onClick={handleSubmitForReview}
            disabled={submitting}
            className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-500 disabled:opacity-50"
          >
            {submitting ? "Submitting…" : "Submit for review"}
          </button>
        )}
      </div>
    </div>
  );
};

export default SkillEditor;
